import React, { useEffect, useState, useCallback } from "react";
import { ArrowLeft, BookOpen, Loader2, AlertCircle, Calendar, Eye } from "lucide-react";
import axios from "axios";
import BetaChip from "./BetaChip";

const API = process.env.REACT_APP_BACKEND_URL || "";

const SITE = "https://hexawebos.com";

function fmtDate(v) {
  if (!v) return "";
  const d = new Date(v);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
}

/** keywords can arrive as a real array or as a stringified list. */
function asList(v) {
  if (Array.isArray(v)) return v;
  if (typeof v === "string" && v.trim()) {
    try {
      const p = JSON.parse(v.replace(/'/g, '"'));
      if (Array.isArray(p)) return p;
    } catch (_) { /* fall through */ }
    return v.split(",").map((s) => s.trim()).filter(Boolean);
  }
  return [];
}

function setMeta(name, content) {
  let m = document.querySelector(`meta[name="${name}"]`);
  if (!m) {
    m = document.createElement("meta");
    m.setAttribute("name", name);
    document.head.appendChild(m);
  }
  m.setAttribute("content", content || "");
}

function setJsonLd(payload) {
  let script = document.getElementById("hexa-blog-jsonld");
  if (!script) {
    script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "hexa-blog-jsonld";
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(payload);
}

export default function BlogPage({ onBack, slug = null, onNavigate }) {
  const [posts, setPosts] = useState([]);
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const go = useCallback(
    (to) => {
      if (typeof onNavigate === "function") onNavigate(to);
      else window.location.assign(to);
    },
    [onNavigate]
  );

  // ---- data ----------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");

    const url = slug
      ? `${API}/api/blog/posts/${encodeURIComponent(slug)}`
      : `${API}/api/blog/posts`;

    axios
      .get(url)
      .then((r) => {
        if (cancelled) return;
        if (slug) {
          setPost(r.data || null);
        } else {
          setPosts(Array.isArray(r.data) ? r.data : []);
        }
      })
      .catch((e) => {
        if (cancelled) return;
        setError(
          e?.response?.status === 404
            ? "That post could not be found. It may have been unpublished."
            : "We couldn't load the blog just now. Please refresh in a moment."
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [slug]);

  // ---- SEO -----------------------------------------------------------------
  useEffect(() => {
    // Set canonical link
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }

    if (slug && post) {
      document.title = `${post.title} — HEXA OS Blog`;
      canonical.setAttribute("href", `${SITE}/blog/${post.slug || slug}`);
      setMeta("description", post.meta_description || "");
      const kw = asList(post.keywords);
      if (kw.length) setMeta("keywords", kw.join(", "));
      setJsonLd({
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headline: post.title,
        description: post.meta_description || "",
        image: post.cover_image || undefined,
        datePublished: post.published_at || post.created,
        dateModified: post.updated || post.published_at || post.created,
        mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE}/blog/${post.slug}` },
        keywords: kw.join(", ") || undefined,
        publisher: { "@type": "Organization", name: "HEXA", url: SITE },
      });
      return;
    }

    canonical.setAttribute("href", `${SITE}/blog`);
    document.title = "Blog — HEXA OS";
    setMeta(
      "description",
      "HEXA OS Blog — stories, product updates, AI research and behind-the-scenes from the team building the AI-native web OS."
    );
    setJsonLd([
      {
        "@context": "https://schema.org",
        "@type": "Blog",
        name: "HEXA OS Blog",
        url: `${SITE}/blog`,
        description: "Product updates, AI research and behind-the-scenes from HEXA OS.",
        inLanguage: "en",
        publisher: { "@type": "Organization", name: "HEXA", url: SITE },
        blogPost: posts.map((p) => ({
          "@type": "BlogPosting",
          headline: p.title,
          description: p.meta_description || "",
          datePublished: p.published_at || p.created,
          url: `${SITE}/blog/${p.slug}`,
        })),
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
          { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE}/blog` },
        ],
      },
    ]);
  }, [slug, post, posts]);

  useEffect(
    () => () => {
      const s = document.getElementById("hexa-blog-jsonld");
      if (s) s.remove();
    },
    []
  );

  // ---- shell ---------------------------------------------------------------
  const Shell = ({ children }) => (
    <div
      className="text-white"
      style={{
        position: "fixed",
        inset: 0,
        overflowY: "auto",
        background: "#070b1a",
        WebkitOverflowScrolling: "touch",
      }}
      data-testid="page-blog"
    >
      <div className="max-w-3xl mx-auto px-6 py-14">{children}</div>
    </div>
  );

  // ---- detail view ---------------------------------------------------------
  if (slug) {
    return (
      <Shell>
        <button
          data-testid="blog-back-to-list"
          onClick={() => go("/blog")}
          className="mono text-[10px] uppercase tracking-[0.28em] text-white/60 hover:text-white flex items-center gap-1 mb-8"
        >
          <ArrowLeft size={12} /> All posts
        </button>

        {loading && (
          <div className="flex items-center gap-2 text-white/60 py-16" data-testid="blog-detail-loading">
            <Loader2 size={16} className="animate-spin" /> Loading post…
          </div>
        )}

        {!loading && error && (
          <div
            className="flex items-start gap-3 rounded-lg p-5"
            data-testid="blog-detail-error"
            style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.30)" }}
          >
            <AlertCircle size={18} className="text-red-300 mt-0.5" />
            <div>
              <div className="font-semibold mb-1">{error}</div>
              <button onClick={() => go("/blog")} className="text-[#a78bfa] hover:underline text-sm">
                Back to all posts
              </button>
            </div>
          </div>
        )}

        {!loading && !error && post && (
          <article data-testid="blog-detail">
            {post.cover_image ? (
              <img
                src={post.cover_image}
                alt={post.title}
                className="w-full rounded-xl mb-8 object-cover"
                style={{ maxHeight: 380 }}
              />
            ) : null}

            <h1 className="text-3xl sm:text-4xl font-bold leading-tight mb-4">{post.title}</h1>

            <div className="flex items-center gap-4 mono text-[10px] uppercase tracking-[0.24em] text-white/45 mb-10">
              {fmtDate(post.published_at || post.created) && (
                <span className="flex items-center gap-1.5">
                  <Calendar size={11} /> {fmtDate(post.published_at || post.created)}
                </span>
              )}
              {typeof post.views === "number" && (
                <span className="flex items-center gap-1.5">
                  <Eye size={11} /> {post.views} views
                </span>
              )}
            </div>

            <div className="flex items-center gap-2 mb-8" data-testid="blog-share-bar">
              <a
                data-testid="blog-share-linkedin"
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(`${SITE}/api/blog/share/${post.slug || slug}`)}`}
                target="_blank" rel="noopener noreferrer"
                className="mono text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 rounded border border-white/15 text-white/70 hover:text-white hover:border-white/40"
              >Share on LinkedIn</a>
              <button
                data-testid="blog-share-copy"
                type="button"
                onClick={() => { try { navigator.clipboard.writeText(`${SITE}/api/blog/share/${post.slug || slug}`); } catch (_) { /* ignore */ } }}
                className="mono text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 rounded border border-white/15 text-white/70 hover:text-white hover:border-white/40"
              >Copy share link</button>
            </div>

            {post.content_html ? (
              <div
                className="hexa-blog-content"
                data-testid="blog-detail-body"
                dangerouslySetInnerHTML={{ __html: post.content_html }}
              />
            ) : (
              <p className="text-white/70 whitespace-pre-line leading-relaxed">{post.content_md || ""}</p>
            )}

            {asList(post.keywords).length > 0 && (
              <div className="flex flex-wrap gap-2 mt-12 pt-8" style={{ borderTop: "1px solid rgba(255,255,255,0.10)" }}>
                {asList(post.keywords).map((k) => (
                  <span
                    key={k}
                    className="mono text-[10px] px-2.5 py-1 rounded-full text-white/60"
                    style={{ background: "rgba(139,92,246,0.10)", border: "1px solid rgba(139,92,246,0.25)" }}
                  >
                    {k}
                  </span>
                ))}
              </div>
            )}
          </article>
        )}
      </Shell>
    );
  }

  // ---- list view -----------------------------------------------------------
  return (
    <Shell>
      <button
        data-testid="blog-back"
        onClick={onBack}
        className="mono text-[10px] uppercase tracking-[0.28em] text-white/60 hover:text-white flex items-center gap-1 mb-8"
      >
        <ArrowLeft size={12} /> Back to HEXA
      </button>

      <div className="flex items-center gap-3 mb-2">
        <BookOpen size={22} className="text-[#a78bfa]" />
        <h1 className="text-3xl font-bold">HEXA Blog</h1>
        <BetaChip tone="outline" />
      </div>
      <div className="mono text-[10px] uppercase tracking-[0.28em] text-white/45 mb-10">
        Product updates · AI research · Field notes
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-white/60 py-12" data-testid="blog-loading">
          <Loader2 size={16} className="animate-spin" /> Loading posts…
        </div>
      )}

      {!loading && error && (
        <div
          className="flex items-start gap-3 rounded-lg p-5"
          data-testid="blog-error"
          style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.30)" }}
        >
          <AlertCircle size={18} className="text-red-300 mt-0.5" />
          <div className="font-semibold">{error}</div>
        </div>
      )}

      {!loading && !error && posts.length === 0 && (
        <div
          className="rounded-lg p-8 text-center"
          data-testid="blog-empty"
          style={{
            background: "linear-gradient(140deg, rgba(139,92,246,0.10), rgba(56,189,248,0.06))",
            border: "1px solid rgba(139,92,246,0.28)",
          }}
        >
          <h2 className="text-2xl font-semibold mb-3">No posts published yet.</h2>
          <p className="text-white/70 leading-relaxed max-w-xl mx-auto">
            Published posts from Blog Studio will appear here automatically.
          </p>
        </div>
      )}

      {!loading && !error && posts.length > 0 && (
        <div className="space-y-4" data-testid="blog-list">
          {posts.map((p) => (
            <button
              key={p.id || p.slug}
              data-testid={`blog-card-${p.slug}`}
              onClick={() => go(`/blog/${p.slug}`)}
              className="w-full text-left rounded-xl p-5 transition hover:brightness-125"
              style={{
                background: "rgba(255,255,255,0.03)",
                border: "1px solid rgba(139,92,246,0.22)",
              }}
            >
              {p.cover_image ? (
                <img
                  src={p.cover_image}
                  alt={p.title}
                  className="w-full rounded-lg mb-4 object-cover"
                  style={{ maxHeight: 200 }}
                />
              ) : null}

              <h2 className="text-xl font-semibold leading-snug mb-2">{p.title}</h2>

              {p.meta_description ? (
                <p className="text-white/70 text-sm leading-relaxed mb-3">{p.meta_description}</p>
              ) : null}

              <div className="flex items-center gap-4 mono text-[10px] uppercase tracking-[0.24em] text-white/45">
                {fmtDate(p.published_at || p.created) && (
                  <span className="flex items-center gap-1.5">
                    <Calendar size={11} /> {fmtDate(p.published_at || p.created)}
                  </span>
                )}
                <span className="text-[#a78bfa]">Read →</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </Shell>
  );
}
